interface PresignedResponse {
    url: string;
    headers: Record<string, string[]>;
    key: string;
}

export interface UploadItem {
    id: string;
    file: File;
    s3Key: string | null;
    progress: number;
    status: 'pending' | 'uploading' | 'done' | 'error';
    caption: string;
    error?: string;
    abort?: () => void;
    retry?: () => void;
}

export function uploadFile(
    file: File,
    onProgress: (percent: number) => void,
): { promise: Promise<string>; abort: () => void } {
    let xhr: XMLHttpRequest | null = null;
    let aborted = false;

    const promise = (async () => {
        const xsrfToken = document.cookie
            .split('; ')
            .find(c => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1];

        const response = await fetch('/uploads/presigned-url', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(xsrfToken ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken) } : {}),
            },
            body: JSON.stringify({
                filename: file.name,
                content_type: file.type,
                size: file.size,
            }),
        });

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message ?? `Failed to get presigned URL (${response.status})`);
        }

        const presigned: PresignedResponse = await response.json();

        if (aborted) throw new DOMException('Upload aborted', 'AbortError');

        return new Promise<string>((resolve, reject) => {
            xhr = new XMLHttpRequest();
            xhr.open('PUT', presigned.url);

            // Set headers from presigned response
            for (const [name, values] of Object.entries(presigned.headers)) {
                // Skip host and content-length headers — the browser sets these
                if (['host', 'content-length'].includes(name.toLowerCase())) continue;
                xhr.setRequestHeader(name, values.join(', '));
            }

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };

            xhr.onload = () => {
                if (xhr!.status >= 200 && xhr!.status < 300) {
                    resolve(presigned.key);
                } else {
                    reject(new Error(`Upload failed (${xhr!.status})`));
                }
            };

            xhr.onerror = () => reject(new Error('Upload failed (network error)'));
            xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));

            xhr.send(file);
        });
    })();

    return {
        promise,
        abort: () => {
            aborted = true;
            xhr?.abort();
        },
    };
}
